import * as THREE from 'three';
import {
  createGlobeScene,
  disposeGlobe,
  applyGlobeTexture,
  CAMERA_INITIAL_Z,
  type GlobeScene,
} from './globe';
import { createControls, type ControlsHandle, type CameraTarget } from './controls';
import { LayerManager } from './layer-manager';
import {
  TextureManager,
  isMobileDevice,
  maxTierForGpu,
  prefersSaveData,
  TIER_ORDER,
  type TextureTier,
} from '../textures';
import { loadCountries, loadCountryPolygons, loadAdmin1, type Country } from '../data';
import {
  bordersCache,
  buildBorderLines,
  buildBorderLinesFromPositions,
  decodeAdmin1Rings,
  decodeBorderPositions,
  decodeCountryPolygons,
  decodeCountryRings,
  disposeBorderLines,
} from '../borders';
import { PolygonFillManager, triangulateGroups } from '../fills';
import { CountryHitTester, InteractionDispatcher, type MapClickEvent } from '../interactions';
import { MarkerManager, markerStyles } from '../markers';
import { ArcManager } from '../arcs';


export interface MapEngineOptions {
  /** Element to mount the WebGL canvas into. The engine takes over its size. */
  container: HTMLElement;
  /**
   * Base URL for texture files. Defaults to the production CDN
   * (cdn.atlasguess.com). Override for local dev/staging or if textures
   * move to a different host.
   */
  textureBaseUrl?: string;
  /**
   * Pan (drag-rotate) sensitivity multiplier. 1.0 = engine default.
   * Intended to be driven by a user-facing slider (range ~0.5–2.0).
   * Clamped internally to [0.1, 5.0]. Auto-scales with zoom internally —
   * just set the raw multiplier you want at the default zoom level.
   */
  panSensitivity?: number;
  /**
   * Zoom (wheel + pinch) sensitivity multiplier. 1.0 = engine default.
   * Because zoom is multiplicative, this scales the per-step percentage
   * change rather than a raw pixel delta. Clamped to [0.1, 5.0].
   */
  zoomSensitivity?: number;
  /** Fired when a texture tier finishes loading (initial + each upgrade). */
  onTextureLoad?: (event: { tier: TextureTier; durationMs: number }) => void;
  /** Fired when a texture fetch fails — typically logged, not user-facing. */
  onTextureError?: (error: Error, tier: TextureTier) => void;
  /**
   * Skip the photo-texture fetch entirely. Modes that hide the surface
   * sphere (e.g. country-tap on a flat-fill globe) save the bandwidth
   * and decode time. The surface mesh still exists, just stays untextured;
   * callers can still flip it visible later via `engine.layers` and
   * trigger an on-demand load if we add that API.
   */
  skipTexture?: boolean;
  /**
   * Fired when the hovered country changes (including null when leaving).
   * Desktop-only — mobile devices skip hover entirely and this callback
   * never fires there. Guard with `setHoverEnabled(false)` to suppress.
   */
  onCountryHover?: (country: Country | null) => void;
  /**
   * Fired on a click over the globe. Country is null if the click hit
   * ocean or an unassigned region. Guard with `setClickEnabled(false)`.
   */
  onCountryClick?: (country: Country | null) => void;
  /**
   * Fired on a click over the globe with raw lat/lon AND the resolved
   * country (null for ocean / unassigned). City-distance modes consume
   * lat/lon; modes that need both can use this in place of
   * `onCountryClick`. Fires on every click including ocean — country
   * will simply be null. Guard with `setClickEnabled(false)`.
   */
  onMapClick?: (event: MapClickEvent) => void;
}

/**
 * Options for `MapEngine.loadBorders`. Both subsystems default to enabled
 * to match the original behavior; city-distance modes that only need the
 * borders mesh for a post-guess overlay opt out of both to skip the
 * triangulation + PIP precompute (the bulk of the work).
 */
export interface LoadBordersOptions {
  /** Build the per-country fill mesh. Default true. Disable for modes that never highlight country polygons. */
  buildFills?: boolean;
  /** Build the country hit-tester (PIP). Default true. Disable for modes that don't resolve clicks to a country. */
  buildHitTester?: boolean;
}

/**
 * The public handle to a mounted map engine. Create one per visible globe
 * (usually one per page). Always call `dispose()` on unmount to release
 * GPU resources and detach event listeners.
 */
export class MapEngine {
  private readonly container: HTMLElement;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: GlobeScene;
  private readonly controls: ControlsHandle;
  private readonly resizeObserver: ResizeObserver;
  private readonly textureManager: TextureManager;
  private rafHandle = 0;
  private disposed = false;

  // FPS tracking — smoothed exponential moving average across frames.
  private lastFrameTime = 0;
  private smoothedFPS = 60;

  // Last-loaded tier, for debug / HUD display.
  private currentTier: TextureTier | null = null;

  // Highest tier we've requested so far (whether loaded yet or in flight).
  // Drives the zoom-based progressive upgrade ladder so we never request
  // the same tier twice and never re-request a lower one.
  private highestRequestedTier: TextureTier | null = null;
  // True while a tier-upgrade fetch is in flight; suppresses duplicate kicks.
  private tierUpgradeInFlight = false;
  // Throttle the per-frame check so we don't burn cycles re-evaluating zoom.
  private lastUpgradeCheckMs = 0;

  // Borders mesh, created once when loadBorders resolves. Null until then.
  private bordersMesh: THREE.LineSegments | null = null;
  private bordersLoading = false;

  // Performance timings (filled in as phases complete) — exposed via
  // getPerfTimings() for the debug panel's perf section.
  private perfConstructorMs: number | null = null;
  private perfTextureFetchByTier = new Map<TextureTier, number>();
  private perfPolygonsFetchMs: number | null = null;
  private perfPolygonsDecodeMs: number | null = null;
  private perfFillsBuildMs: number | null = null;

  // Admin1 (state/province) meshes, keyed by parent country ISO. Populated
  // lazily — `loadAdmin1('US')` fetches + decodes + creates the mesh on demand.
  private admin1Meshes = new Map<string, THREE.LineSegments>();
  private admin1Loading = new Set<string>();

  // Interactions — built once BOTH metadata and polygons are loaded.
  private interactions: InteractionDispatcher | null = null;
  private readonly options: MapEngineOptions;

  // Country metadata, cached after loadBorders fetches it. Used by the
  // marker auto-place helpers and exposed via getCountries().
  private countries: Country[] | null = null;

  /**
   * Unified marker system. Use `engine.markers.add(...)` to drop a dot at any
   * lat/lon; group markers by `layer` name to show/hide/clear as a set. See
   * docs on `MarkerManager` for the full API.
   */
  readonly markers: MarkerManager;

  /**
   * Animated great-circle arcs between two lat/lon points. Used by
   * city-distance modes to connect guess to answer; reusable for any
   * "path between two points" visualization. See `ArcManager.draw()`.
   */
  readonly arcs: ArcManager;

  /**
   * Per-country fill manager — colored polygons that sit between the texture
   * sphere and the border lines. Built when polygon data finishes loading.
   * Most callers should reach this through `engine.layers.outline.*`.
   */
  private readonly fillsManager: PolygonFillManager;

  /**
   * Layer composition facade. Public access to texture / outline / markers
   * layer toggles and opacity tweens, plus per-country fill state. Mapped
   * one-to-one to design § 7b.4.
   */
  readonly layers: LayerManager;

  constructor(options: MapEngineOptions) {
    const ctorStart = performance.now();
    const { container } = options;
    this.container = container;
    this.options = options;

    const rect = container.getBoundingClientRect();
    const width = rect.width || 1;
    const height = rect.height || 1;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    // DPR cap differs by device class. Modern phones report ratios of 2.5-3+
    // which would burn ~9× the desktop fragment-shading work for negligible
    // sharpness gain at touch viewing distance. Capping mobile at 1.5 cuts
    // pixel count by ~44% vs 2.0 — biggest single perf win on the country
    // mode without visible quality loss.
    const dprCap = isMobileDevice() ? 1.5 : 2;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, dprCap));
    this.renderer.setSize(width, height, false);
    this.renderer.domElement.style.display = 'block';
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';

    container.appendChild(this.renderer.domElement);

    this.scene = createGlobeScene({ width, height });

    this.markers = new MarkerManager(this.scene.globeGroup);
    this.arcs = new ArcManager(this.scene.globeGroup);
    // Seed arcs with the initial canvas resolution — wide-line shading
    // in `LineMaterial` needs pixel dimensions; subsequent resizes update
    // it via handleResize().
    this.arcs.setResolution(width, height);
    this.fillsManager = new PolygonFillManager(this.scene.globeGroup);
    this.layers = new LayerManager({
      surface: this.scene.surface,
      fills: this.fillsManager,
      markers: this.markers,
    });

    this.controls = createControls({
      element: this.renderer.domElement,
      globeGroup: this.scene.globeGroup,
      camera: this.scene.camera,
      initialZoom: CAMERA_INITIAL_Z,
      panSensitivity: options.panSensitivity,
      zoomSensitivity: options.zoomSensitivity,
    });

    // Auto-resize on container size changes (responsive layouts, window resize).
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(container);

    // Progressive texture loading. First paint at `medium` for snappiness,
    // then ladder up to `large` / `xl` as the user zooms in (see
    // maybeUpgradeTier). Mobile / saveData stay at `small` and never upgrade.
    this.textureManager = new TextureManager({
      gpuMaxTextureSize: this.renderer.capabilities.maxTextureSize,
      baseUrl: options.textureBaseUrl,
      onLoad: ({ tier, texture, durationMs }) => {
        if (this.disposed) {
          texture.dispose();
          return;
        }
        applyGlobeTexture(this.scene.surface, texture);
        this.currentTier = tier;
        this.perfTextureFetchByTier.set(tier, durationMs);
        if (this.tierRank(tier) > this.tierRank(this.highestRequestedTier)) {
          this.highestRequestedTier = tier;
        }
        this.tierUpgradeInFlight = false;
        options.onTextureLoad?.({ tier, durationMs });
      },
      onError: (err, tier) => {
        this.tierUpgradeInFlight = false;
        options.onTextureError?.(err, tier);
      },
    });
    if (!options.skipTexture) {
      void this.textureManager.start();
    }

    // Interactions wire up immediately, BEFORE polygons load. Pointer
    // events + raw lat/lon resolution work without polygons; country
    // resolution is null until loadBorders attaches a hit-tester.
    // City-distance modes never call loadBorders; they still need clicks.
    this.interactions = new InteractionDispatcher({
      element: this.renderer.domElement,
      camera: this.scene.camera,
      globeGroup: this.scene.globeGroup,
      hitTester: null,
      markerManager: this.markers,
      onHover: (country) => {
        this.fillsManager.setHoveredIso(country?.iso_a2 ?? null);
        this.options.onCountryHover?.(country);
      },
      onClick: (country) => this.options.onCountryClick?.(country),
      onMapClick: (event) => this.options.onMapClick?.(event),
    });
    this.layers.outline.attachDispatcher(this.interactions);

    // Polygons are NOT auto-loaded — game modes opt in via engine.loadBorders().
    // Distance / city-guess modes never need them; tap / fills modes call
    // loadBorders() at setup. The demo page calls it from Globe.svelte.

    this.rafHandle = requestAnimationFrame((t) => this.frame(t));
    this.perfConstructorMs = performance.now() - ctorStart;
  }

  /**
   * Fetch country-polygons.json + countries.json in parallel, build the
   * border LineSegments mesh, and (by default) the country fill mesh +
   * hit-tester. City-distance modes that only need the borders for a
   * post-guess overlay can pass `{ buildFills: false, buildHitTester: false }`
   * to skip the triangulation + PIP precompute, which is the bulk of the cost.
   *
   * Idempotent — safe to call multiple times; later calls are no-ops
   * regardless of the options on the second call.
   * Promise resolves when borders are visible AND requested subsystems
   * are live.
   */
  async loadBorders(options: LoadBordersOptions = {}): Promise<void> {
    if (this.bordersMesh || this.bordersLoading || this.disposed) return;
    const buildFills = options.buildFills ?? true;
    const buildHitTester = options.buildHitTester ?? true;
    this.bordersLoading = true;
    try {
      // Datasets — `data/loader.ts` already caches per-URL promises, so a
      // second call resolves instantly. We still need the raw polygons
      // dataset for any cache-miss subsystems below (a previous engine
      // may have populated only a subset of the bordersCache).
      const fetchStart = performance.now();
      const [polygonsDataset, countriesDataset] = await Promise.all([
        loadCountryPolygons(),
        loadCountries(),
      ]);
      this.perfPolygonsFetchMs = performance.now() - fetchStart;
      if (this.disposed) return;

      // Cache country metadata for use by marker auto-placement + getCountries().
      this.countries = countriesDataset.countries;

      // Borders visual — cache hit skips the per-vertex 3D projection
      // pass; cache miss runs it once and stores the Float32Array.
      let borderPositions = bordersCache.borderPositions;
      if (!borderPositions) {
        const decodeStart = performance.now();
        const rings = decodeCountryRings(polygonsDataset);
        borderPositions = decodeBorderPositions(rings);
        bordersCache.borderPositions = borderPositions;
        this.perfPolygonsDecodeMs = performance.now() - decodeStart;
      } else {
        this.perfPolygonsDecodeMs = 0;
      }
      const mesh = buildBorderLinesFromPositions(borderPositions);
      this.bordersMesh = mesh;
      this.scene.globeGroup.add(mesh);

      // Country fills — same dataset, holes preserved for enclaves.
      // Skipped for modes that don't tap countries (city-distance), saving
      // a hefty triangulation pass. Cache hit also skips it for cross-mode
      // navigation between any two country/quiz modes.
      if (buildFills) {
        let baked = bordersCache.fillBaked;
        if (!baked) {
          const polygonGroups = decodeCountryPolygons(polygonsDataset);
          const fillsStart = performance.now();
          baked = triangulateGroups(polygonGroups);
          bordersCache.fillBaked = baked;
          this.perfFillsBuildMs = performance.now() - fillsStart;
        } else {
          this.perfFillsBuildMs = 0;
        }
        this.fillsManager.buildFromBaked(baked);
      }

      // Attach the country hit-tester to the dispatcher (which already
      // exists from the engine constructor — pointer events have been
      // flowing the whole time, just without country resolution). Skipped
      // for city-distance modes that only need raw lat/lon clicks.
      // CountryHitTester.find() is read-only, so the cached instance can
      // safely be wired into successive engines' dispatchers.
      if (buildHitTester) {
        let hitTester = bordersCache.hitTester;
        if (!hitTester) {
          hitTester = new CountryHitTester(polygonsDataset, countriesDataset.countries);
          bordersCache.hitTester = hitTester;
        }
        this.interactions?.setHitTester(hitTester);
      }

      // Hand the borders mesh to the layer manager so any pre-load
      // setBordersVisible calls take effect now.
      this.layers.outline.attachBorders(mesh);

      // Fills default to hidden — game modes turn them on when needed.
      // Matches the pre-fills baseline (texture + borders only) so the
      // user doesn't see washed-out tints on every country at first paint.
      this.layers.outline.setFillsVisible(false);

      // One-line diagnostic to make cross-mode cache behavior visible
      // (matches the `[atlas] <tier> texture ready` style). Triangulation
      // is the dominant cost; if `fillsBuildMs` is 0 we hit the cache.
      console.info(
        `[atlas] borders ready — decode ${this.perfPolygonsDecodeMs ?? 0}ms, ` +
          `fills ${this.perfFillsBuildMs ?? 0}ms` +
          (buildHitTester ? ', hit-tester' : '') +
          (buildFills ? '' : ' (fills skipped)'),
      );
    } finally {
      this.bordersLoading = false;
    }
  }

  /**
   * Enable / disable country-hover event emission. When disabled, the HUD
   * never shows "hovering over X"; calls to `onCountryHover` stop firing.
   * Per design doc § 7a — per-game-mode configurable.
   *
   * @deprecated Use `engine.layers.outline.setHoverEnabled(b)`.
   */
  setHoverEnabled(enabled: boolean): void {
    this.layers.outline.setHoverEnabled(enabled);
  }

  /** Enable / disable click-to-country event emission. */
  setClickEnabled(enabled: boolean): void {
    this.interactions?.setClickEnabled(enabled);
  }

  /**
   * Replace the click handler that fires on every globe click with raw
   * lat/lon + resolved country. Pass `undefined` to detach. Game modes
   * call this on mount and again with `undefined` on unmount so the
   * shared engine doesn't keep firing into a destroyed component.
   */
  setOnMapClick(handler: ((event: MapClickEvent) => void) | undefined): void {
    this.options.onMapClick = handler;
  }

  /** Replace the country-only click handler. Pass `undefined` to detach. */
  setOnCountryClick(handler: ((country: Country | null) => void) | undefined): void {
    this.options.onCountryClick = handler;
  }

  /** Replace the country-hover handler. Pass `undefined` to detach. */
  setOnCountryHover(handler: ((country: Country | null) => void) | undefined): void {
    this.options.onCountryHover = handler;
  }

  /**
   * Override the shared layer-transition duration (default 400ms). Affects
   * subsequent layer setOpacity / setVisible calls; tweens already in flight
   * keep their original duration.
   */
  setLayerTransitionMs(ms: number): void {
    this.layers.setTransitionMs(ms);
  }

  /**
   * Programmatically frame the globe at a (lat, lon, zoom). Pass
   * `animate: true` to ease via the existing tween; otherwise the camera
   * snaps instantly so the next frame already shows the new framing
   * (used by region-filtered quiz modes for initial-paint centering).
   */
  setCamera(target: CameraTarget): void {
    this.controls.setCameraTarget(target);
  }

  /**
   * Whether COUNTRY interactions are wired up (i.e. polygons loaded and a
   * hit-tester is attached). Pointer events + raw lat/lon clicks work
   * from engine construction; only country resolution waits on this.
   * Kept as `getInteractionsReady` for back-compat with the debug panel.
   */
  getInteractionsReady(): boolean {
    return this.interactions?.hasHitTester() === true;
  }

  /**
   * Lazy-load admin1 (state/province) borders for a specific country and
   * render them on the globe. Idempotent — calling again for an already-
   * loaded country is a no-op. Phase 1 ships with `'US'` available; other
   * countries 404 until their data files exist.
   *
   * Rendered in the same warm cream as country borders but at opacity 0.3
   * so they read as a secondary hierarchy nested inside already-opaque
   * country outlines.
   */
  async loadAdmin1(iso: string): Promise<void> {
    const normalizedIso = iso.toUpperCase();
    if (
      this.admin1Meshes.has(normalizedIso) ||
      this.admin1Loading.has(normalizedIso) ||
      this.disposed
    ) {
      return;
    }
    this.admin1Loading.add(normalizedIso);
    try {
      const dataset = await loadAdmin1(normalizedIso);
      if (this.disposed) return;
      const rings = decodeAdmin1Rings(dataset);
      const mesh = buildBorderLines(rings, { opacity: 0.3 });
      mesh.name = `admin1.${normalizedIso}`;
      this.admin1Meshes.set(normalizedIso, mesh);
      this.scene.globeGroup.add(mesh);
    } finally {
      this.admin1Loading.delete(normalizedIso);
    }
  }

  /** Show or hide admin1 borders for a given country. No-op if not loaded. */
  setAdmin1Visible(iso: string, visible: boolean): void {
    const mesh = this.admin1Meshes.get(iso.toUpperCase());
    if (mesh) mesh.visible = visible;
  }

  /** Whether admin1 borders for this country are currently showing. */
  getAdmin1Visible(iso: string): boolean {
    return this.admin1Meshes.get(iso.toUpperCase())?.visible === true;
  }

  /** Whether admin1 data has finished loading + rendering for this country. */
  getAdmin1Ready(iso: string): boolean {
    return this.admin1Meshes.has(iso.toUpperCase());
  }

  /**
   * Country metadata once loadBorders has resolved. Null before that. Used
   * by game modes that need the full country list (picking random target,
   * filtering by continent, etc.).
   */
  getCountries(): Country[] | null {
    return this.countries;
  }

  /**
   * Convenience: place a dot at the centroid of every country flagged
   * `needs_dot: true` — the tiny states (Vatican, Monaco, Tuvalu, etc.)
   * that are otherwise hard or impossible to see at normal zoom. The
   * `needs_dot` flag is computed in the build pipeline based on the
   * largest sub-polygon size.
   *
   * Markers go into the given `layer` (default `country_pins`), so the
   * whole set can be toggled together via
   * `engine.markers.setLayerVisible(layer, bool)`.
   *
   * No-op if country data isn't loaded yet.
   */
  autoPlaceNeedsDotMarkers(layer = 'country_pins'): void {
    if (!this.countries) return;
    for (const country of this.countries) {
      if (!country.needs_dot) continue;
      this.markers.add({
        id: `country-pin-${country.iso_a2}`,
        position: { lat: country.centroid[1], lon: country.centroid[0] },
        style: markerStyles.countryPin,
        layer,
        data: country,
      });
    }
  }

  /**
   * Show or hide the borders without destroying the mesh. Toggle freely.
   *
   * @deprecated Use `engine.layers.outline.setBordersVisible(b)`.
   */
  setBordersVisible(visible: boolean): void {
    this.layers.outline.setBordersVisible(visible);
  }

  /**
   * Current borders visibility state. False if borders haven't loaded yet.
   *
   * @deprecated Use `engine.layers.outline.isBordersVisible()`.
   */
  getBordersVisible(): boolean {
    return this.layers.outline.isBordersVisible();
  }

  /** Whether the borders mesh has finished loading and attached to the scene. */
  getBordersReady(): boolean {
    return this.bordersMesh !== null;
  }

  /** Current frames-per-second, smoothed across frames. Read per-frame for HUD display. */
  getFPS(): number {
    return Math.round(this.smoothedFPS);
  }

  /** Tier of the texture currently on the globe, or null before first load. */
  getCurrentTextureTier(): TextureTier | null {
    return this.currentTier;
  }

  /**
   * Force-load a specific texture tier, replacing whatever's currently on the
   * globe. Used by the debug panel for A/B visual comparison across tiers.
   * Resolves when the swap is complete (or throws on fetch failure).
   */
  async loadTier(tier: TextureTier): Promise<void> {
    await this.textureManager.loadTier(tier);
  }

  /**
   * Max texture size the current GPU reports it can allocate, in pixels.
   * Informational — drives auto-tier selection. Common values:
   * 16384 (most modern GPUs), 8192 (mid-tier mobile), 32768 (high-end desktop).
   */
  getGpuMaxTextureSize(): number {
    return this.renderer.capabilities.maxTextureSize;
  }

  /** Release all WebGL resources, detach listeners, remove canvas. Safe to call twice. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    cancelAnimationFrame(this.rafHandle);
    this.textureManager.dispose();
    this.resizeObserver.disconnect();
    this.controls.dispose();
    this.interactions?.dispose();
    this.interactions = null;
    if (this.bordersMesh) {
      this.scene.globeGroup.remove(this.bordersMesh);
      disposeBorderLines(this.bordersMesh);
      this.bordersMesh = null;
    }
    for (const mesh of this.admin1Meshes.values()) {
      this.scene.globeGroup.remove(mesh);
      disposeBorderLines(mesh);
    }
    this.admin1Meshes.clear();
    this.fillsManager.dispose();
    this.arcs.dispose();
    this.markers.dispose();
    disposeGlobe(this.scene);
    this.renderer.dispose();

    if (this.renderer.domElement.parentElement === this.container) {
      this.container.removeChild(this.renderer.domElement);
    }
  }

  /**
   * Resize handling. The canvas container is pinned to the viewport (see
   * mode-frame.scss `__canvas { position: fixed }`) so the sidebar's
   * collapse/expand transition never changes container dimensions —
   * ResizeObserver only fires on actual window resize.
   *
   * rAF-coalesce is kept as a defensive measure: rapid window-edge drags
   * can produce multiple resize events between paints; collapsing them
   * to one setSize avoids redundant WebGL drawbuffer reallocations.
   */
  private resizeRafScheduled = false;
  private handleResize(): void {
    if (this.resizeRafScheduled || this.disposed) return;
    this.resizeRafScheduled = true;
    requestAnimationFrame(() => {
      this.resizeRafScheduled = false;
      if (this.disposed) return;
      const rect = this.container.getBoundingClientRect();
      const width = rect.width || 1;
      const height = rect.height || 1;
      this.renderer.setSize(width, height, false);
      this.scene.camera.aspect = width / height;
      this.scene.camera.updateProjectionMatrix();
      // Wide-line arcs need the new resolution so their shader-side
      // width math stays in CSS pixels.
      this.arcs.setResolution(width, height);
    });
  }

  private frame(now: number): void {
    if (this.disposed) return;

    if (this.lastFrameTime !== 0) {
      const delta = now - this.lastFrameTime;
      if (delta > 0) {
        const instantaneous = 1000 / delta;
        // Exponential moving average — 10% weight to newest frame.
        this.smoothedFPS = this.smoothedFPS * 0.9 + instantaneous * 0.1;
      }
    }
    this.lastFrameTime = now;

    this.controls.tick();
    this.interactions?.tick();
    this.arcs.tick(now);
    this.maybeUpgradeTier(now);
    this.renderer.render(this.scene.scene, this.scene.camera);

    this.rafHandle = requestAnimationFrame((t) => this.frame(t));
  }

  // Camera distances (globe radius = 1) at which we ladder up to the next
  // texture tier. Tunable; chosen by eye against the camera-zoom range
  // [CAMERA_ZOOM_MIN=1.5, CAMERA_ZOOM_MAX=8] with default initial 3.2.
  private static readonly LARGE_TIER_DISTANCE = 2.5;
  private static readonly XL_TIER_DISTANCE = 1.8;
  private static readonly UPGRADE_CHECK_INTERVAL_MS = 100;

  /**
   * Per-frame: if the camera has zoomed in past a tier-upgrade threshold,
   * kick off the next-higher texture fetch. Throttled to 10Hz; suppressed
   * during in-flight upgrades; capped by GPU max + saveData / mobile signals.
   *
   * Tiers are sticky — once requested, never downgraded. Loaded textures
   * live in GPU memory for the session; replacing a higher tier with a
   * lower one if the user zooms back out costs more than it saves.
   */
  private maybeUpgradeTier(now: number): void {
    if (now - this.lastUpgradeCheckMs < MapEngine.UPGRADE_CHECK_INTERVAL_MS) return;
    this.lastUpgradeCheckMs = now;

    if (this.tierUpgradeInFlight) return;
    // saveData is an explicit user signal — respect it, never auto-upgrade.
    if (prefersSaveData()) return;

    const dist = this.scene.camera.position.length();
    const ceiling = maxTierForGpu(this.renderer.capabilities.maxTextureSize);

    // Mobile ladder is shifted one tier down from desktop so the bandwidth
    // hits stay reasonable on cellular while the UX still ladders smoothly:
    //
    //   moderate zoom (dist < 2.5): desktop → large,   mobile → medium
    //   deep zoom    (dist < 1.8):  desktop → xl,      mobile → large
    //
    // saveData users still bail above. GPU max via `ceiling` clamps both.
    const mobile = isMobileDevice();
    const tierAtModerateZoom: TextureTier = mobile ? 'medium' : 'large';
    const tierAtDeepZoom: TextureTier = mobile ? 'large' : 'xl';

    let target: TextureTier | null = null;
    if (
      dist < MapEngine.XL_TIER_DISTANCE &&
      this.tierRank(tierAtDeepZoom) <= this.tierRank(ceiling)
    ) {
      target = tierAtDeepZoom;
    } else if (
      dist < MapEngine.LARGE_TIER_DISTANCE &&
      this.tierRank(tierAtModerateZoom) <= this.tierRank(ceiling)
    ) {
      target = tierAtModerateZoom;
    }
    if (target === null) return;
    if (this.tierRank(target) <= this.tierRank(this.highestRequestedTier)) return;

    this.highestRequestedTier = target;
    this.tierUpgradeInFlight = true;
    void this.textureManager.loadTier(target);
  }

  /**
   * Snapshot of phase timings filled in as engine init progresses. Returned
   * by-value so callers can poll without holding a live reference. Values
   * are `null` until the corresponding phase completes.
   */
  getPerfTimings(): PerfTimings {
    return {
      engineConstructorMs: this.perfConstructorMs,
      textures: Object.fromEntries(
        [...this.perfTextureFetchByTier.entries()].map(([tier, ms]) => [tier, { fetchMs: ms }]),
      ) as PerfTimings['textures'],
      polygonsFetchMs: this.perfPolygonsFetchMs,
      polygonsDecodeMs: this.perfPolygonsDecodeMs,
      fillsBuildMs: this.perfFillsBuildMs,
    };
  }

  /** Numeric rank of a tier, with `null` ranking below every real tier. */
  private tierRank(tier: TextureTier | null): number {
    if (tier === null) return -1;
    return TIER_ORDER.indexOf(tier);
  }
}

/**
 * Phase timings exposed by `engine.getPerfTimings()`. Each numeric field is
 * milliseconds, or `null` if the phase hasn't completed yet. The textures
 * map fills in entries as each tier's load resolves (so initial paint adds
 * `medium`, then `large` on first zoom-up, then `xl` on second zoom-up).
 */
export interface PerfTimings {
  engineConstructorMs: number | null;
  textures: Partial<Record<TextureTier, { fetchMs: number }>>;
  polygonsFetchMs: number | null;
  polygonsDecodeMs: number | null;
  fillsBuildMs: number | null;
}
